import { css, Global } from '@emotion/react';
import PlusIcon from '@icons/plus.svg?react';
import { Tooltip } from '@ui/tooltip';
import { rem } from '@utils';
import type { ReactNode } from 'react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Styled from './PromptMenu.styled';

export interface PromptMenuItem {
  disabled?: boolean;
  label: string;
  icon: ReactNode;
  tooltip?: ReactNode;
  width?: string;
  onClick?: () => void;
  widget?: (ctx: { close: () => void }) => ReactNode;
}

interface Props {
  disabled?: boolean;
  items: PromptMenuItem[];
  onClose?: () => void;
}

/**
 * @description null: closed,
 * @description 'items': open items menu,
 * @description number: open widget menu
 */
type OpenState = null | 'items' | number;

function PromptMenu({ disabled, items, onClose }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [openState, setOpenState] = useState<OpenState>(null);
  const [widgetPlacement, setWidgetPlacement] = useState<'above' | 'below'>(
    'below'
  );
  const widgetSlotRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLMenuElement>(null);

  useEffect(() => {
    if (openState === 'items') {
      const style = getComputedStyle(document.body);
      const maxHeight = Number.parseFloat(
        style.getPropertyValue('--prompt-menu-max-height')
      );
      const actionPanelHeight = Number.parseFloat(
        style.getPropertyValue('--action-panel-height')
      );
      const bottom = wrapperRef.current?.getBoundingClientRect().bottom || 0;
      const spaceBelow = window.innerHeight - bottom - actionPanelHeight;

      setWidgetPlacement(spaceBelow >= maxHeight + 8 ? 'below' : 'above');
    }
  }, [openState]);

  const close = useCallback(() => {
    setOpenState(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (openState === null) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (
        !wrapperRef.current?.contains(target) &&
        !menuRef.current?.contains(target) &&
        !widgetSlotRef.current?.contains(target)
      ) {
        close();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openState, close]);

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }

    setOpenState(openState === null ? 'items' : null);
  };

  const handleItemClick = (item: PromptMenuItem, index: number) => {
    if (item.disabled) {
      return;
    }

    item.onClick?.();

    if (!item.widget) {
      close();
      return;
    }

    setOpenState(index);
  };

  const activeItem =
    typeof openState === 'number' ? items[openState] : undefined;

  return (
    <Fragment>
      <Styled.Wrapper ref={wrapperRef}>
        <Styled.Button
          disabled={disabled}
          selected={openState !== null}
          onClick={handleButtonClick}>
          <PlusIcon />
        </Styled.Button>
      </Styled.Wrapper>
      <Styled.Menu hidden={openState !== 'items'} ref={menuRef}>
        {items.map((item, index) => {
          const menuItem = (
            <Styled.MenuItem
              key={item.label}
              disabled={item.disabled}
              onClick={() => handleItemClick(item, index)}>
              {item.icon}
              <span>{item.label}</span>
            </Styled.MenuItem>
          );

          return item.tooltip ? (
            <Tooltip
              className="add-audience-button-tooltip-anchor"
              classesPopper="add-audience-button-tooltip-popper"
              classesTooltip="add-audience-button-tooltip"
              content={item.tooltip}
              enterDelay={0}
              key={item.label}
              leaveDelay={0}
              placement="top-start">
              {menuItem}
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </Styled.Menu>
      {activeItem?.widget && (
        <Styled.WidgetSlot
          placement={widgetPlacement}
          ref={widgetSlotRef}
          width={activeItem.width}>
          {activeItem.widget({ close })}
        </Styled.WidgetSlot>
      )}
      <Global styles={styles} />
    </Fragment>
  );
}

const styles = css`
  .add-audience-button-tooltip-anchor {
    cursor: default;
  }

  .add-audience-button-tooltip {
    && {
      line-height: ${rem(16)};
      margin-bottom: ${rem(16)} !important;
      margin-left: ${rem(-8)} !important;
    }
  }

  .add-audience-button-tooltip-popper {
    && {
      max-width: 264px;
    }
  }
`;

export default PromptMenu;
